// oxlint-disable max-lines, max-lines-per-function, max-statements, max-params

type ArrayMergeOptions = {
  // options are captured in the closure provided by toArrayMergeOptions
  cloneUnlessOtherwiseSpecified: (value: object) => object;
  isMergeableObject: (value: object) => boolean;
};

// InternalOptions makes the optional Options fields required for internal use.
// Omit is used to replace (not intersect) the optional variants from Options.
type InternalOptions = Omit<Options, 'arrayMerge' | 'isMergeableObject'> & {
  arrayMerge: (
    target: Array<any>,
    source: Array<any>,
    options: ArrayMergeOptions,
  ) => Array<any>;
  cloneUnlessOtherwiseSpecified: (value: any, options: InternalOptions) => any;
  isMergeableObject: (value: object) => boolean;
};

type MergeableRecord = Record<PropertyKey, any>;
type MergeFunction = (
  target: any,
  source: any,
  options: InternalOptions,
) => any;

type Options = {
  arrayMerge?: (
    target: Array<any>,
    source: Array<any>,
    options?: ArrayMergeOptions,
  ) => Array<any>;
  clone?: boolean;
  customMerge?: (
    key: string,
    options?: Options,
  ) => ((x: any, y: any, options?: Options) => any) | undefined;
  isMergeableObject?: (value: object) => boolean;
};

// Sorted alphabetically per perfectionist/sort-union-types.
type TypedArray =
  | BigInt64Array
  | BigUint64Array
  | Float32Array
  | Float64Array
  | Int8Array
  | Int16Array
  | Int32Array
  | Uint8Array
  | Uint8ClampedArray
  | Uint16Array
  | Uint32Array;

const hasOwn = Object.prototype.hasOwnProperty;
const isEnumerable = Object.prototype.propertyIsEnumerable;
const { getOwnPropertySymbols } = Object;

function cloneArrayBuffer(value: ArrayBuffer): ArrayBuffer {
  return cloneBufferBytes(value);
}

function cloneBufferBytes(
  buffer: ArrayBufferLike,
  byteOffset = 0,
  byteLength = buffer.byteLength - byteOffset,
): ArrayBuffer {
  const clone = new ArrayBuffer(byteLength);
  new Uint8Array(clone).set(new Uint8Array(buffer, byteOffset, byteLength));

  return clone;
}

function cloneDataView(value: DataView): DataView {
  return new DataView(
    cloneBufferBytes(value.buffer, value.byteOffset, value.byteLength),
  );
}

function cloneDefault(value: any): any {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (value instanceof RegExp || value instanceof Date) {
    return value;
  }
  if (Array.isArray(value)) {
    return mergeArrayFast([], value);
  }
  if (isMap(value)) {
    return mergeMapFast(new Map(), value);
  }
  if (isSet(value)) {
    return mergeSetFast(new Set(), value);
  }
  if (isTypedArray(value)) {
    return cloneTypedArray(value);
  }
  if (isDataView(value)) {
    return cloneDataView(value);
  }
  if (value instanceof ArrayBuffer) {
    return cloneArrayBuffer(value);
  }
  if (isPlainObject(value)) {
    return mergePlainObjectFast({}, value);
  }

  return mergeObjectGeneric({}, value, defaultOptions);
}

function cloneTypedArray(value: TypedArray): TypedArray {
  const Ctor = value.constructor as any;

  return new Ctor(value as any);
}

function cloneUnlessOtherwiseSpecified(
  value: any,
  options: InternalOptions,
): any {
  if (options.clone === false || !options.isMergeableObject(value)) {
    return value;
  }
  if (isTypedArray(value)) {
    return cloneTypedArray(value);
  }
  if (isDataView(value)) {
    return cloneDataView(value);
  }
  if (value instanceof ArrayBuffer) {
    return cloneArrayBuffer(value);
  }

  return mergeWithOptions(emptyTarget(value), value, options);
}

function concatTypedArrays(target: TypedArray, source: TypedArray): TypedArray {
  const Ctor = target.constructor as any;
  const result = new Ctor(target.length + source.length);
  result.set(target, 0);
  result.set(source, target.length);

  return result;
}

function copyClonedKeys(
  source: any,
  destination: MergeableRecord,
  options: InternalOptions,
): void {
  const keys = Object.keys(source);
  for (const key of keys) {
    destination[key] = options.cloneUnlessOtherwiseSpecified(
      source[key],
      options,
    );
  }

  const symbols = getOwnPropertySymbols(source);
  for (const key of symbols) {
    if (isEnumerable.call(source, key)) {
      destination[key] = options.cloneUnlessOtherwiseSpecified(
        source[key],
        options,
      );
    }
  }
}

function defaultArrayMerge(
  target: Array<any>,
  source: Array<any>,
  options: ArrayMergeOptions,
): Array<any> {
  const targetLength = target.length;
  const sourceLength = source.length;
  const result = Array.from<any>({ length: targetLength + sourceLength });

  for (let index = 0; index < targetLength; index += 1) {
    result[index] = options.cloneUnlessOtherwiseSpecified(target[index]);
  }

  for (let index = 0; index < sourceLength; index += 1) {
    result[targetLength + index] = options.cloneUnlessOtherwiseSpecified(
      source[index],
    );
  }

  return result;
}

function defaultIsMergeableObject(value: object): boolean {
  return isNonNullObject(value) && !isSpecialObject(value);
}

function emptyTarget(value: any): any {
  if (Array.isArray(value)) {
    return [];
  }
  if (isMap(value)) {
    return new Map();
  }
  if (isSet(value)) {
    return new Set();
  }

  return {};
}

function getMergeFunction(
  key: PropertyKey,
  options: InternalOptions,
): MergeFunction {
  if (!options.customMerge) {
    return deepmerge as MergeFunction;
  }
  const customMerge = options.customMerge(key as never);

  return typeof customMerge === 'function'
    ? (customMerge as MergeFunction)
    : (deepmerge as MergeFunction);
}

function isDataView(value: unknown): value is DataView {
  return value instanceof DataView;
}

function isMap(value: unknown): value is Map<any, any> {
  return value instanceof Map;
}

function isNonNullObject(value: unknown): value is MergeableRecord {
  return !!value && typeof value === 'object';
}

function isPlainObject(value: unknown): value is MergeableRecord {
  if (!isNonNullObject(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
}

function isSet(value: unknown): value is Set<any> {
  return value instanceof Set;
}

function isSpecialObject(value: MergeableRecord): boolean {
  return value instanceof RegExp || value instanceof Date;
}

function isTypedArray(value: unknown): value is TypedArray {
  return ArrayBuffer.isView(value) && !isDataView(value);
}

function mergeArrayFast(target: Array<any>, source: Array<any>): Array<any> {
  const targetLength = target.length;
  const sourceLength = source.length;
  const result = Array.from<any>({ length: targetLength + sourceLength });

  for (let index = 0; index < targetLength; index += 1) {
    result[index] = cloneDefault(target[index]);
  }

  for (let index = 0; index < sourceLength; index += 1) {
    result[targetLength + index] = cloneDefault(source[index]);
  }

  return result;
}

function mergeDefault(target: any, source: any): any {
  const sourceIsArray = Array.isArray(source);
  const targetIsArray = Array.isArray(target);
  if (sourceIsArray !== targetIsArray) {
    return cloneDefault(source);
  }
  if (sourceIsArray) {
    return mergeArrayFast(target, source);
  }

  const sourceIsMap = isMap(source);
  const targetIsMap = isMap(target);
  if (sourceIsMap !== targetIsMap) {
    return cloneDefault(source);
  }
  if (sourceIsMap) {
    return mergeMapFast(target, source);
  }

  const sourceIsSet = isSet(source);
  const targetIsSet = isSet(target);
  if (sourceIsSet !== targetIsSet) {
    return cloneDefault(source);
  }
  if (sourceIsSet) {
    return mergeSetFast(target, source);
  }

  const sourceIsTypedArray = isTypedArray(source);
  const targetIsTypedArray = isTypedArray(target);
  if (sourceIsTypedArray !== targetIsTypedArray) {
    return cloneDefault(source);
  }
  if (sourceIsTypedArray) {
    return mergeTypedArrayFast(target, source);
  }

  const sourceIsDataView = isDataView(source);
  const targetIsDataView = isDataView(target);
  if (sourceIsDataView !== targetIsDataView) {
    return cloneDefault(source);
  }
  if (sourceIsDataView) {
    return cloneDefault(source);
  }

  const sourceIsArrayBuffer = source instanceof ArrayBuffer;
  const targetIsArrayBuffer = target instanceof ArrayBuffer;
  if (sourceIsArrayBuffer !== targetIsArrayBuffer) {
    return cloneDefault(source);
  }
  if (sourceIsArrayBuffer) {
    return cloneDefault(source);
  }

  if (isPlainObject(target) && isPlainObject(source)) {
    return mergePlainObjectFast(target, source);
  }

  return mergeObjectGeneric(target, source, defaultOptions);
}

function mergeMapFast(
  target: Map<any, any>,
  source: Map<any, any>,
): Map<any, any> {
  const destination = new Map<any, any>();

  for (const [key, value] of target) {
    destination.set(key, cloneDefault(value));
  }

  for (const [key, value] of source) {
    destination.set(
      key,
      target.has(key) && defaultIsMergeableObject(value)
        ? mergeDefault(target.get(key), value)
        : cloneDefault(value),
    );
  }

  return destination;
}

function mergeMapWithOptions(
  target: Map<any, any>,
  source: Map<any, any>,
  options: InternalOptions,
): Map<any, any> {
  const destination = new Map<any, any>();

  for (const [key, value] of target) {
    destination.set(key, options.cloneUnlessOtherwiseSpecified(value, options));
  }

  for (const [key, value] of source) {
    destination.set(
      key,
      target.has(key) && options.isMergeableObject(value)
        ? mergeWithOptions(target.get(key), value, options)
        : options.cloneUnlessOtherwiseSpecified(value, options),
    );
  }

  return destination;
}

function mergeObjectGeneric(
  target: any,
  source: any,
  options: InternalOptions,
): MergeableRecord {
  const destination: MergeableRecord = {};

  if (options.isMergeableObject(target)) {
    copyClonedKeys(target, destination, options);
  }

  mergeSourceKeys(target, source, destination, options);

  return destination;
}

function mergePlainObjectFast(
  target: MergeableRecord,
  source: MergeableRecord,
): MergeableRecord {
  const destination: MergeableRecord = {};

  // Copy target keys, but skip keys that also exist in source (those will be merged below,
  // avoiding a redundant deep-clone that would immediately be overwritten).
  const targetKeys = Object.keys(target);
  for (const key of targetKeys) {
    if (!hasOwn.call(source, key)) {
      destination[key] = cloneDefault(target[key]);
    }
  }

  const targetSymbols = getOwnPropertySymbols(target);
  for (const key of targetSymbols) {
    if (isEnumerable.call(target, key)) {
      destination[key] = cloneDefault(target[key]);
    }
  }

  mergeSourceKeysFast(target, source, destination);

  return destination;
}

function mergeSetFast(target: Set<any>, source: Set<any>): Set<any> {
  const destination = new Set<any>();

  for (const value of target) {
    destination.add(cloneDefault(value));
  }

  for (const value of source) {
    destination.add(cloneDefault(value));
  }

  return destination;
}

function mergeSetWithOptions(
  target: Set<any>,
  source: Set<any>,
  options: InternalOptions,
): Set<any> {
  const destination = new Set<any>();

  for (const value of target) {
    destination.add(options.cloneUnlessOtherwiseSpecified(value, options));
  }

  for (const value of source) {
    destination.add(options.cloneUnlessOtherwiseSpecified(value, options));
  }

  return destination;
}

function mergeSourceKeys(
  target: any,
  source: any,
  destination: MergeableRecord,
  options: InternalOptions,
): void {
  const keys = Object.keys(source);
  for (const key of keys) {
    if (propertyIsUnsafe(target, key)) {
      continue;
    }
    const value = source[key];
    destination[key] =
      propertyIsOnObject(target, key) && options.isMergeableObject(value)
        ? getMergeFunction(key, options)(target[key], value, options)
        : options.cloneUnlessOtherwiseSpecified(value, options);
  }

  const symbols = getOwnPropertySymbols(source);
  for (const key of symbols) {
    if (!isEnumerable.call(source, key) || propertyIsUnsafe(target, key)) {
      continue;
    }
    const value = source[key];
    destination[key] =
      propertyIsOnObject(target, key) && options.isMergeableObject(value)
        ? getMergeFunction(key, options)(target[key], value, options)
        : options.cloneUnlessOtherwiseSpecified(value, options);
  }
}

function mergeSourceKeysFast(
  target: MergeableRecord,
  source: MergeableRecord,
  destination: MergeableRecord,
): void {
  const keys = Object.keys(source);
  for (const key of keys) {
    if (propertyIsUnsafe(target, key)) {
      continue;
    }
    const value = source[key];
    destination[key] =
      propertyIsOnObject(target, key) && defaultIsMergeableObject(value)
        ? mergeDefault(target[key], value)
        : cloneDefault(value);
  }

  const symbols = getOwnPropertySymbols(source);
  for (const key of symbols) {
    if (!isEnumerable.call(source, key) || propertyIsUnsafe(target, key)) {
      continue;
    }
    const value = source[key];
    destination[key] =
      propertyIsOnObject(target, key) && defaultIsMergeableObject(value)
        ? mergeDefault(target[key], value)
        : cloneDefault(value);
  }
}

function mergeTypedArrayFast(target: any, source: any): any {
  return target.constructor === source.constructor
    ? concatTypedArrays(target, source)
    : cloneDefault(source);
}

function propertyIsOnObject(object: any, property: PropertyKey): boolean {
  try {
    return property in object;
  } catch {
    return false;
  }
}

function propertyIsUnsafe(target: any, key: PropertyKey): boolean {
  return (
    propertyIsOnObject(target, key) &&
    !(hasOwn.call(target, key) && isEnumerable.call(target, key))
  );
}

const defaultOptions: InternalOptions = {
  arrayMerge: defaultArrayMerge,
  cloneUnlessOtherwiseSpecified,
  isMergeableObject: defaultIsMergeableObject,
};

function canUseDefaultFastPath(options?: Options): boolean {
  return (
    !options ||
    (options.arrayMerge === undefined &&
      options.customMerge === undefined &&
      options.isMergeableObject === undefined &&
      options.clone !== false)
  );
}

function deepmerge<T>(x: Partial<T>, y: Partial<T>, options?: Options): T;
function deepmerge<T1, T2>(
  x: Partial<T1>,
  y: Partial<T2>,
  options?: Options,
): T1 & T2;
function deepmerge(target: any, source: any, options?: Options): any {
  if (canUseDefaultFastPath(options)) {
    return mergeDefault(target, source);
  }

  return mergeWithOptions(target, source, normalizeOptions(options as Options));
}

function deepmergeAll(objects: Array<object>, options?: Options): object {
  if (!Array.isArray(objects)) {
    throw new TypeError('first argument should be an array');
  }

  let result: any = {};
  for (const object of objects) {
    result = deepmerge(result, object, options);
  }

  return result;
}

function mergeWithOptions(
  target: any,
  source: any,
  options: InternalOptions,
): any {
  const sourceIsArray = Array.isArray(source);
  const targetIsArray = Array.isArray(target);
  if (sourceIsArray !== targetIsArray) {
    return options.cloneUnlessOtherwiseSpecified(source, options);
  }
  if (sourceIsArray) {
    return options.arrayMerge(target, source, toArrayMergeOptions(options));
  }

  const sourceIsMap = isMap(source);
  const targetIsMap = isMap(target);
  if (sourceIsMap !== targetIsMap) {
    return options.cloneUnlessOtherwiseSpecified(source, options);
  }
  if (sourceIsMap) {
    return mergeMapWithOptions(target, source, options);
  }

  const sourceIsSet = isSet(source);
  const targetIsSet = isSet(target);
  if (sourceIsSet !== targetIsSet) {
    return options.cloneUnlessOtherwiseSpecified(source, options);
  }
  if (sourceIsSet) {
    return mergeSetWithOptions(target, source, options);
  }

  const sourceIsTypedArray = isTypedArray(source);
  const targetIsTypedArray = isTypedArray(target);
  if (sourceIsTypedArray !== targetIsTypedArray) {
    return options.cloneUnlessOtherwiseSpecified(source, options);
  }
  if (sourceIsTypedArray) {
    return target.constructor === source.constructor
      ? concatTypedArrays(target, source)
      : options.cloneUnlessOtherwiseSpecified(source, options);
  }

  const sourceIsDataView = isDataView(source);
  const targetIsDataView = isDataView(target);
  if (sourceIsDataView !== targetIsDataView) {
    return options.cloneUnlessOtherwiseSpecified(source, options);
  }
  if (sourceIsDataView) {
    return options.cloneUnlessOtherwiseSpecified(source, options);
  }

  const sourceIsArrayBuffer = source instanceof ArrayBuffer;
  const targetIsArrayBuffer = target instanceof ArrayBuffer;
  if (sourceIsArrayBuffer !== targetIsArrayBuffer) {
    return options.cloneUnlessOtherwiseSpecified(source, options);
  }

  return sourceIsArrayBuffer
    ? options.cloneUnlessOtherwiseSpecified(source, options)
    : mergeObjectGeneric(target, source, options);
}

function normalizeOptions(options: Options): InternalOptions {
  // exactOptionalPropertyTypes: optional fields cannot be explicitly set to undefined
  // in an object literal, so we assign them conditionally.
  const normalized: InternalOptions = {
    arrayMerge: options.arrayMerge ?? defaultArrayMerge,
    cloneUnlessOtherwiseSpecified,
    isMergeableObject: options.isMergeableObject ?? defaultIsMergeableObject,
  };
  if (options.clone !== undefined) {
    normalized.clone = options.clone;
  }
  if (options.customMerge !== undefined) {
    normalized.customMerge = options.customMerge;
  }

  return normalized;
}
// Adapts InternalOptions to the ArrayMergeOptions interface for user-provided
// arrayMerge callbacks. cloneUnlessOtherwiseSpecified closes over the real
// InternalOptions so callers do not need to pass them explicitly.
function toArrayMergeOptions(options: InternalOptions): ArrayMergeOptions {
  return {
    cloneUnlessOtherwiseSpecified: (value: object) =>
      options.cloneUnlessOtherwiseSpecified(value, options),
    isMergeableObject: options.isMergeableObject,
  };
}

export { deepmerge, deepmergeAll };
export type { ArrayMergeOptions, Options };
